import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import BankScopeField from '../../components/BankScopeField';
import LotterySearchModal from '../../components/LotterySearchModal';
import TicketReceiptModal from '../../components/TicketReceiptModal';
import DateField from '../../components/DateField';
import { revertTicket, searchRevertTickets } from '../../api/revertTicket';
import { resolveLotteryByCode } from '../../utils/lotteryLookup';
import { todayISO } from '../../utils/date';
import { bankLabel } from '../../utils/bank';
import './RevertNullifiedTicketPage.css';

// Portail (2026-09-26) : la MEME page sert la company (configuration par defaut
// ci-dessous) et le superviseur (pages/supervisor/supervisorCentralPortal.jsx :
// SupervisorLayout, API limitee a SES banks, champ Banque superviseur).
const COMPANY_PORTAL = {
  Layout,
  homePath: '/dashboard',
  centralPath: '/central',
  BankField: BankScopeField,
  search: searchRevertTickets,
  action: revertTicket,
};

const RevertNullifiedTicketPage = ({ portal = COMPANY_PORTAL }) => {
  const [date, setDate] = useState(todayISO());
  const [bank, setBank] = useState('');
  const [bankName, setBankName] = useState('');
  const [searchBy, setSearchBy] = useState('Online');
  const [sequence, setSequence] = useState('');
  const [numero, setNumero] = useState('');
  const [lottery, setLottery] = useState('');
  const [lotteryName, setLotteryName] = useState('');
  const [lotteryLookupError, setLotteryLookupError] = useState('');
  const [showLotteryModal, setShowLotteryModal] = useState(false);

  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [revertingId, setRevertingId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!bank || (!sequence && !numero)) {
      setError('Renseigne la banque et au moins la sequence ou le N° de fiche.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await portal.search({
        bank, sq: sequence, no: numero, tirage: lottery, searchBy, date,
      });
      setResults(data);
      setSearched(true);
    } catch (err) {
      setError('Recherche impossible.');
    } finally {
      setLoading(false);
    }
  };

  // Quitter le champ Loterie : resout le code tape -> nom affiche a cote.
  const handleLotteryBlur = async () => {
    const c = String(lottery).trim();
    if (c === '' || c === '0') { setLotteryLookupError(''); return; }
    const res = await resolveLotteryByCode(c);
    if (res) {
      setLottery(res.code);
      setLotteryName(res.name);
      setLotteryLookupError('');
    } else {
      setLotteryLookupError('Loterie introuvable');
    }
  };

  const handleRevert = async (ticketId) => {
    setRevertingId(ticketId);
    setError('');
    try {
      await portal.action(ticketId);
      setResults((prev) => prev.filter((t) => t.id !== ticketId));
      setSelectedTicket(null);
    } catch (err) {
      setError('Reactivation impossible.');
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <portal.Layout>
      <p className="rt-breadcrumb">
        Ruta : <Link to={portal.homePath} className="bc-link">Menu</Link>
        {' >> '}
        <Link to={portal.centralPath} className="bc-link">Central</Link>
        {' >> '}
        <span className="bc-current">Revert Nullified Ticket</span>
      </p>

      <div className="rt-card">
        <div className="rt-card-header">
          <span className="rt-card-title">Revert Ticket d'annulation</span>
        </div>
        <div className="rt-card-body">
          <form onSubmit={handleSearch} className="rt-form">
            <div className="rt-fields">
              <div className="rt-col">
                <div className="rt-row">
                  <label className="rt-label">Banque</label>
                  <portal.BankField
                    id={bank}
                    name={bankName}
                    onResolve={(id, nm) => { setBank(id); setBankName(nm); }}
                    inputClass="rt-input rt-input-sm"
                    iconClass="rt-icon-btn"
                    nameClass="rt-bank-name"
                    errClass="rt-lookup-err"
                  />
                </div>

                <div className="rt-row">
                  <label className="rt-label">Recherche par</label>
                  <select className="rt-select" value={searchBy} onChange={e => setSearchBy(e.target.value)}>
                    <option value="Online">En ligne</option>
                    <option value="Local">Local</option>
                  </select>
                </div>

                <div className="rt-row">
                  <label className="rt-label">Loterie</label>
                  <input
                    className="rt-input rt-input-sm"
                    type="text"
                    inputMode="numeric"
                    value={lottery}
                    onChange={e => { setLottery(e.target.value); setLotteryLookupError(''); }}
                    onFocus={e => e.target.select()}
                    onBlur={handleLotteryBlur}
                  />
                  <span className="rt-icon-btn" onClick={() => setShowLotteryModal(true)}>🔍</span>
                  {lotteryLookupError
                    ? <span className="rt-lookup-err">{lotteryLookupError}</span>
                    : lotteryName && <span className="rt-bank-name">{lotteryName}</span>}
                </div>
              </div>

              <div className="rt-col">
                <div className="rt-row">
                  <label className="rt-label">Date</label>
                  <DateField className="rt-input rt-input-date" value={date} onChange={setDate} />
                </div>

                <div className="rt-row">
                  <label className="rt-label">Sequence</label>
                  <input
                    className="rt-input rt-input-sm"
                    type="text"
                    value={sequence}
                    onChange={e => setSequence(e.target.value)}
                  />
                </div>

                <div className="rt-row">
                  <label className="rt-label">N° Fiche</label>
                  <input
                    className="rt-input rt-input-sm"
                    type="text"
                    inputMode="numeric"
                    value={numero}
                    onChange={e => setNumero(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {showLotteryModal && (
              <LotterySearchModal
                allowAll={false}
                onSelect={(code, name) => {
                  setLottery(String(code));
                  setLotteryName(name || '');
                  setLotteryLookupError('');
                }}
                onClose={() => setShowLotteryModal(false)}
              />
            )}

            <button type="submit" className="rt-search-btn" disabled={loading}>
              {loading ? 'Recherche...' : 'Chercher'}
            </button>

            {error && <p className="rt-error">{error}</p>}
          </form>

          {searched && (
            <table className="rt-table">
              <thead>
                <tr>
                  <th>SQ</th>
                  <th>No</th>
                  <th>Banque</th>
                  <th>Loterie</th>
                  <th>Total</th>
                  <th>Date</th>
                  <th>Heure</th>
                </tr>
              </thead>
              <tbody>
                {results.map((ticket) => (
                  <tr key={ticket.id} className="rt-row-hover" onClick={() => setSelectedTicket(ticket)}>
                    <td>{ticket.sq}</td>
                    <td>{ticket.no}</td>
                    <td>{bankLabel(ticket.bank_seq, ticket.bank_name)}</td>
                    <td>{ticket.tirage_name}</td>
                    <td>{ticket.total_amount}</td>
                    <td>{new Date(ticket.created_at).toLocaleDateString()}</td>
                    <td>{new Date(ticket.created_at).toLocaleTimeString()}</td>
                  </tr>
                ))}
                {results.length === 0 && (
                  <tr><td colSpan={7} className="rt-empty">Aucune fiche annulée trouvée.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedTicket && (
        <TicketReceiptModal
          ticket={selectedTicket}
          bank={selectedTicket.bank}
          bankName={selectedTicket.bank_name}
          onClose={() => setSelectedTicket(null)}
          onConfirm={() => handleRevert(selectedTicket.id)}
          confirmLabel="Retourner cette fiche"
          confirmHint="Cette fiche va etre retournee a son etat avant annulation (reactivee)."
          confirming={revertingId === selectedTicket.id}
        />
      )}
    </portal.Layout>
  );
};

export default RevertNullifiedTicketPage;
